import React, { useEffect, useState, useCallback } from "react";
import { api, API, fmtDate, formatError, TIPOS_COMBUSTIVEL, COMBUSTIVEL_LABEL } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, EmptyState, Badge } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Eye, MessageCircle, Printer, Trash2, Fuel } from "lucide-react";

// Reconstrói approval_link e whatsapp_link usando a origem do navegador atual
// (preview ou produção). Garante que o tesoureiro abra a URL correta.
const TREAS_PHONE = "5593991752280";
function buildMessage(d, link) {
  return (
    "COOMFLONA - Solicitação de Combustível\n\n" +
    `Motorista: ${d.motorista_nome}\n` +
    `Veículo: ${d.veiculo || ""} (${d.placa})\n` +
    `Combustível: ${d.tipo_combustivel}\n` +
    `Quantidade: ${d.quantidade} litros\n` +
    `Protocolo: ${d.protocolo}\n\n` +
    "Aprovar ou rejeitar:\n" +
    link
  );
}
function enrichLinks(d) {
  if (!d || !d.approval_token) return d;
  const link = `${window.location.origin}/aprovar-combustivel/${d.approval_token}`;
  const msg = buildMessage(d, link);
  const enc = encodeURIComponent(msg);
  return {
    ...d,
    approval_link: link,
    whatsapp_link: `https://wa.me/${TREAS_PHONE}?text=${enc}`,
    whatsapp_link_alt: `https://api.whatsapp.com/send?phone=${TREAS_PHONE}&text=${enc}`,
    whatsapp_message: msg,
  };
}

const empty = { motorista_nome: "", motorista_documento: "", vehicle_id: "", veiculo: "", placa: "", tipo_combustivel: "Diesel", quantidade: 20, justificativa: "", hodometro_atual: 0, posto: "" };

const statusBadge = (s) => {
  const map = {
    pendente: { v: "warn", t: "Pendente" },
    aprovado: { v: "success", t: "Aprovado" },
    rejeitado: { v: "danger", t: "Rejeitado" },
    utilizado: { v: "green", t: "Utilizado" },
    expirado: { v: "default", t: "Expirado" },
  };
  const c = map[s] || map.pendente;
  return <Badge variant={c.v}>{c.t}</Badge>;
};

export default function FuelRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [justCreated, setJustCreated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, s] = await Promise.all([
        api.get("/fuel-requests"),
        api.get("/vehicles"),
        api.get("/fuel-requests/dashboard/stats"),
      ]);
      setItems(a.data.map(enrichLinks)); setVehicles(b.data); setStats(s.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pickVehicle = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    setForm({
      ...form,
      vehicle_id: vid,
      veiculo: v ? `${v.marca || ""} ${v.modelo}`.trim() : "",
      placa: v ? v.placa : form.placa,
      tipo_combustivel: v ? v.combustivel_padrao : form.tipo_combustivel,
      hodometro_atual: v ? v.hodometro : form.hodometro_atual,
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        placa: (form.placa || "").toUpperCase().trim(),
        quantidade: parseFloat(form.quantidade) || 0,
        hodometro_atual: parseFloat(form.hodometro_atual) || 0,
        vehicle_id: form.vehicle_id || null,
      };
      const { data } = await api.post("/fuel-requests", payload);
      toast.success(`Solicitação ${data.protocolo} criada`);
      setOpen(false); setForm(empty);
      // Reconstrói os links usando a origem atual (preview vs produção)
      const enriched = enrichLinks(data);
      setJustCreated(enriched);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const printReceipt = (it) => { window.open(`/imprimir/combustivel/${it.id}`, "_blank"); };

  const remove = async (id) => {
    if (!confirm("Excluir esta requisição?")) return;
    try { await api.delete(`/fuel-requests/${id}`); toast.success("Excluída"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="fuel-page">
      <SectionTitle action={
        <Btn onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-fuel-btn">
          <Plus className="w-4 h-4" />Nova Requisição
        </Btn>
      }>Requisição de Combustível · <Fuel className="inline w-5 h-5 -mt-1" /></SectionTitle>

      {/* Dashboard */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <StatBox label="Pendentes" value={stats.pendentes} variant="warn" />
          <StatBox label="Aprovadas" value={stats.aprovadas} variant="success" />
          <StatBox label="Rejeitadas" value={stats.rejeitadas} variant="danger" />
          <StatBox label="Utilizadas" value={stats.utilizadas} variant="green" />
          <StatBox label="Litros Liberados" value={`${stats.litros_liberados.toLocaleString("pt-BR")} L`} variant="orange" />
        </div>
      )}

      {stats && stats.by_vehicle.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="brutal-card p-4">
            <div className="font-display font-bold uppercase text-sm tracking-wider mb-3 pb-2 border-b-2 border-[#111]">Consumo por Veículo</div>
            <table className="w-full text-sm">
              <tbody>{stats.by_vehicle.map((v, i) => (
                <tr key={i} className="border-b border-[#EAE8E0]">
                  <td className="py-1.5 font-mono-data font-bold">{v.placa}</td>
                  <td className="text-[#555] text-xs">{v.veiculo}</td>
                  <td className="text-right font-mono-data">{v.litros} L</td>
                  <td className="text-right font-mono-data text-xs text-[#777]">({v.qtd}x)</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="brutal-card p-4">
            <div className="font-display font-bold uppercase text-sm tracking-wider mb-3 pb-2 border-b-2 border-[#111]">Consumo por Unidade</div>
            <table className="w-full text-sm">
              <tbody>{stats.by_unidade.map((u, i) => (
                <tr key={i} className="border-b border-[#EAE8E0]">
                  <td className="py-1.5 font-medium">{u.unidade}</td>
                  <td className="text-right font-mono-data">{u.litros} L</td>
                  <td className="text-right font-mono-data text-xs text-[#777]">({u.qtd}x)</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Protocolo</th><th>Data</th><th>Motorista</th><th>Veículo</th><th>Placa</th><th>Combustível</th><th className="text-right">Litros</th><th>Status</th><th>Aprovador</th><th className="w-40">Ações</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && items.length === 0 && <EmptyState message="Nenhuma requisição de combustível." cols={10} />}
            {items.map((it) => (
              <tr key={it.id} data-testid={`fuel-${it.protocolo}`}>
                <td className="font-mono-data font-bold">{it.protocolo}</td>
                <td className="font-mono-data text-xs">{fmtDate(it.created_at)}</td>
                <td>{it.motorista_nome}</td>
                <td className="text-sm text-[#555]">{it.veiculo}</td>
                <td className="font-mono-data text-xs">{it.placa}</td>
                <td className="text-sm">{COMBUSTIVEL_LABEL[it.tipo_combustivel] || it.tipo_combustivel}</td>
                <td className="text-right font-mono-data font-bold">{it.quantidade} L</td>
                <td>{statusBadge(it.status)}</td>
                <td className="text-xs text-[#555]">{it.approved_by_name || (it.status === "rejeitado" ? "—" : "")}</td>
                <td>
                  <div className="flex gap-1">
                    {it.status === "pendente" && (
                      <>
                        <a href={it.whatsapp_link} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-[#1A7F37] hover:text-white border border-[#111] inline-flex items-center" title="Enviar via WhatsApp" data-testid={`wa-${it.protocolo}`}><MessageCircle className="w-3.5 h-3.5" /></a>
                        <a href={it.approval_link} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111] inline-flex items-center" title="Abrir link de aprovação"><Eye className="w-3.5 h-3.5" /></a>
                      </>
                    )}
                    {(it.status === "aprovado" || it.status === "utilizado") && (
                      <button onClick={() => printReceipt(it)} className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111]" title="Imprimir comprovante"><Printer className="w-3.5 h-3.5" /></button>
                    )}
                    <button onClick={() => setDetail(it)} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]" title="Ver detalhes"><Eye className="w-3.5 h-3.5" /></button>
                    {user?.role === "admin" && (
                      <button onClick={() => remove(it.id)} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Nova Requisição de Combustível">
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <Field label="Motorista *" required value={form.motorista_nome} onChange={(v) => setForm({ ...form, motorista_nome: v })} />
          <Field label="Documento (CPF/CNH)" value={form.motorista_documento} onChange={(v) => setForm({ ...form, motorista_documento: v })} />
          <SelectField label="Veículo (cadastrado)" value={form.vehicle_id} onChange={pickVehicle}
            options={[{ value: "", label: "— Texto livre —" }, ...vehicles.filter((v) => v.ativo).map((v) => ({ value: v.id, label: `${v.placa} · ${v.marca || ""} ${v.modelo}` }))]} />
          <Field label="Veículo (texto)" value={form.veiculo} onChange={(v) => setForm({ ...form, veiculo: v })} placeholder="Toyota Hilux" />
          <Field label="Placa *" required value={form.placa} onChange={(v) => setForm({ ...form, placa: v.toUpperCase() })} placeholder="ABC-1234" />
          <SelectField label="Combustível *" required value={form.tipo_combustivel} onChange={(v) => setForm({ ...form, tipo_combustivel: v })}
            options={TIPOS_COMBUSTIVEL.map((t) => ({ value: t, label: COMBUSTIVEL_LABEL[t] }))} />
          <Field label="Quantidade (litros) *" type="number" required value={form.quantidade} onChange={(v) => setForm({ ...form, quantidade: v })} />
          <Field label="Hodômetro (km)" type="number" value={form.hodometro_atual} onChange={(v) => setForm({ ...form, hodometro_atual: v })} />
          <Field label="Posto de abastecimento" value={form.posto} onChange={(v) => setForm({ ...form, posto: v })} placeholder="Ex: Posto BR — Belterra" testid="fuel-posto" />
          <TextArea label="Justificativa" value={form.justificativa} onChange={(v) => setForm({ ...form, justificativa: v })} placeholder="Viagem Santarém → KM 117, transporte de toras..." colspan />
          <div className="col-span-2 brutal-card-sm p-3 bg-[#FFF8E1] text-xs text-[#7C2D12]">
            Após salvar, será exibido um botão para abrir o WhatsApp do <b>Tesoureiro (Carpegeanne Pantoja · 93 99175-2280)</b> com a mensagem pré-pronta. O link de aprovação expira em 24h.
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border-2 border-[#111] font-display font-bold uppercase text-xs tracking-wider">Cancelar</button>
            <Btn type="submit">Salvar Requisição</Btn>
          </div>
        </form>
      </Modal>

      {/* Modal pós-criação: envio WhatsApp + cópia manual */}
      <Modal open={!!justCreated} onClose={() => setJustCreated(null)} title={justCreated ? `Requisição ${justCreated.protocolo} criada` : ""}>
        {justCreated && (
          <div className="space-y-4">
            <div className="brutal-card-sm p-3 bg-[#E8F0EA]">
              <div className="font-display font-bold uppercase text-sm mb-1">✅ Pronto! Envie ao tesoureiro:</div>
              <div className="text-sm text-[#1F4D34]">Carpegeanne Pantoja · WhatsApp <b>(93) 99175-2280</b></div>
            </div>

            <a
              href={justCreated.whatsapp_link}
              target="_blank"
              rel="noreferrer"
              className="block text-center px-4 py-3 bg-[#1A7F37] text-white font-display font-bold uppercase text-sm tracking-wider hover:bg-[#0F5C28]"
              data-testid="open-wa-link"
            >
              <MessageCircle className="inline w-5 h-5 mr-2 -mt-0.5" />Abrir WhatsApp do Tesoureiro
            </a>

            <a
              href={justCreated.whatsapp_link_alt}
              target="_blank"
              rel="noreferrer"
              className="block text-center px-3 py-2 border-2 border-[#3D6B57] text-[#3D6B57] font-display font-bold uppercase text-[11px] tracking-wider hover:bg-[#3D6B57] hover:text-white"
              data-testid="open-wa-link-alt"
            >
              Alternativa: api.whatsapp.com (se o primeiro não pré-encher)
            </a>

            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Mensagem (caso WhatsApp abra em branco, copie e cole):</div>
              <textarea
                readOnly
                rows={8}
                onFocus={(e) => e.target.select()}
                className="w-full border-2 border-[#111] p-2 text-xs font-mono-data bg-[#F4F8F5]"
                value={justCreated.whatsapp_message || buildMessage(justCreated, justCreated.approval_link)}
                data-testid="wa-message-text"
              />
              <button
                onClick={() => { navigator.clipboard?.writeText(justCreated.whatsapp_message || buildMessage(justCreated, justCreated.approval_link)); toast.success("Mensagem copiada"); }}
                className="mt-2 px-3 py-1.5 border-2 border-[#111] font-display font-bold uppercase text-[10px] tracking-wider hover:bg-[#111] hover:text-white"
              >Copiar mensagem completa</button>
            </div>

            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Apenas o link de aprovação:</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={justCreated.approval_link}
                  className="flex-1 border-2 border-[#111] p-2 text-xs font-mono-data bg-[#F4F8F5]"
                  onFocus={(e) => e.target.select()}
                  data-testid="approval-link-input"
                />
                <button
                  onClick={() => { navigator.clipboard?.writeText(justCreated.approval_link); toast.success("Link copiado"); }}
                  className="px-3 py-2 border-2 border-[#111] font-display font-bold uppercase text-[10px] tracking-wider hover:bg-[#111] hover:text-white"
                >Copiar</button>
              </div>
            </div>

            <div className="text-[11px] text-[#777]">Link expira em 24h. Você também pode reabrir o WhatsApp depois clicando no ícone <MessageCircle className="inline w-3 h-3" /> na linha da requisição.</div>
          </div>
        )}
      </Modal>

      {/* Detail modal */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `Detalhes ${detail.protocolo}` : ""}>
        {detail && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoRow label="Protocolo" value={detail.protocolo} mono />
              <InfoRow label="Status" value={statusBadge(detail.status)} />
              <InfoRow label="Motorista" value={detail.motorista_nome} />
              <InfoRow label="Documento" value={detail.motorista_documento || "—"} />
              <InfoRow label="Veículo" value={detail.veiculo} />
              <InfoRow label="Placa" value={detail.placa} mono />
              <InfoRow label="Combustível" value={COMBUSTIVEL_LABEL[detail.tipo_combustivel] || detail.tipo_combustivel} />
              <InfoRow label="Quantidade" value={`${detail.quantidade} L`} mono />
              <InfoRow label="Hodômetro" value={`${detail.hodometro_atual} km`} mono />
              {detail.posto && <InfoRow label="Posto" value={detail.posto} />}
              <InfoRow label="Criada em" value={fmtDate(detail.created_at)} mono />
              <InfoRow label="Expira em" value={fmtDate(detail.expires_at)} mono />
              <InfoRow label="Aprovado em" value={detail.approved_at ? fmtDate(detail.approved_at) : "—"} mono />
              <InfoRow label="Aprovador" value={detail.approved_by_name || "—"} />
              {detail.qr_used_at && <InfoRow label="QR Utilizado em" value={fmtDate(detail.qr_used_at)} mono />}
            </div>
            {detail.justificativa && <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Justificativa</div><div className="border-2 border-[#111] p-2 text-sm">{detail.justificativa}</div></div>}
            {detail.observacoes_aprovador && <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Obs. do Aprovador</div><div className="border-2 border-[#111] p-2 text-sm">{detail.observacoes_aprovador}</div></div>}
            {detail.rejected_reason && <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#E60000] mb-1">Motivo da Rejeição</div><div className="border-2 border-[#E60000] p-2 text-sm">{detail.rejected_reason}</div></div>}
            {detail.status === "aprovado" && detail.approval_token && (
              <div className="border-2 border-[#3D6B57] p-3 text-center">
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57] mb-2">QR Code para Abastecimento</div>
                <img src={`${API}/public/fuel/${detail.approval_token}/qr.png`} alt="QR" className="mx-auto" style={{ maxWidth: 200 }} />
                <button onClick={() => printReceipt(detail)} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#3D6B57] text-white font-display font-bold uppercase text-xs tracking-wider"><Printer className="w-4 h-4" />Imprimir Comprovante</button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

const VARIANT_BG = {
  warn: "!bg-[#FFC107] !text-[#111]",
  success: "!bg-[#1A7F37] !text-white",
  danger: "!bg-[#E60000] !text-white",
  green: "!bg-[#3D6B57] !text-white",
  orange: "!bg-[#FF4F00] !text-white",
};
function StatBox({ label, value, variant = "warn" }) {
  return (
    <div className={`brutal-card-sm p-3 ${VARIANT_BG[variant] || ""}`}>
      <div className="font-mono-data text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="font-display font-black text-2xl leading-tight">{value}</div>
    </div>
  );
}
function InfoRow({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">{label}</div>
      <div className={mono ? "font-mono-data font-bold" : "font-medium"}>{value}</div>
    </div>
  );
}
